import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, MapPin } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const AREA_NAMES = { A: "Depå A · Pendlarparkering", B: "Depå B · Kolgrillen", C: "Depå C · Parkvägen", D: "Depå D · CIK-parkering", E: "Depå E · Södervägen", F: "Depå F · Badhuset" };

const showOnMap = (area) => {
  const el = document.querySelector(`[data-testid="depot-area-${area.toLowerCase()}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("ring-4", "ring-amber-500");
  setTimeout(() => el.classList.remove("ring-4", "ring-amber-500"), 4000);
};

const ResultCard = ({ r, i }) => (
  <div data-testid={`lookup-result-${i}`} className="rounded-xl border border-amber-500/40 bg-[#14161A] p-4 sm:p-5">
    <div className="font-display text-lg sm:text-xl font-bold uppercase text-white">
      {r.name} – <span className="text-amber-500">Plats {r.spot_number}</span>
    </div>
    <div className="mt-1 text-sm text-white/65">{AREA_NAMES[r.area] || `Depå ${r.area}`}{r.booked_date ? ` · Bokad ${r.booked_date}` : ""}</div>
    <button type="button" onClick={() => showOnMap(r.area)} data-testid={`lookup-show-map-${i}`}
      className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-500 hover:underline">
      <MapPin className="w-4 h-4" /> Visa depå {r.area} på kartan
    </button>
  </div>
);

export const DepotLookup = () => {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  const search = async (e) => {
    e.preventDefault();
    const term = q.trim();
    if (term.replace(/\s+/g, "").length < 3) { setError("Skriv minst 3 tecken."); setResults(null); return; }
    setLoading(true); setError("");
    try { setResults(await api.depotLookup(term)); }
    catch (err) { setResults(null); setError(formatApiErrorDetail(err.response?.data?.detail) || "Sökningen misslyckades. Försök igen."); }
    finally { setLoading(false); }
  };

  return (
    <section id="hitta-min-depaplats" data-testid="depot-lookup" className="pt-12 sm:pt-16 scroll-mt-24">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <div className="rounded-2xl border border-white/10 bg-[#0F1114] p-5 sm:p-8">
          <h2 className="font-display text-2xl sm:text-3xl font-black uppercase text-white">Hitta min depåplats</h2>
          <p className="mt-2 text-sm text-white/60">Har du redan bokat? Sök på namnet eller e-postadressen du bokade med.</p>
          <form onSubmit={search} className="mt-5 flex flex-col sm:flex-row gap-3">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sök på namn eller e-post" data-testid="depot-lookup-input"
              className="h-12 bg-[#14161A] border-white/15 text-white placeholder:text-white/30 text-base" />
            <Button type="submit" disabled={loading} data-testid="depot-lookup-submit" className="h-12 px-8 bg-amber-500 text-black hover:bg-amber-400 font-bold uppercase tracking-widest">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Sök</>}
            </Button>
          </form>
          {error && <p data-testid="depot-lookup-error" className="mt-4 text-sm text-red-400">{error}</p>}
          {results && results.length === 0 && (
            <p data-testid="depot-lookup-empty" className="mt-5 text-sm text-white/70">
              Ingen bokning hittades. Kontrollera stavningen eller <Link to="/kontakt" className="text-amber-500 hover:underline">kontakta arrangören</Link>.
            </p>
          )}
          {results && results.length > 0 && (
            <div data-testid="depot-lookup-results" className="mt-5 space-y-3">
              {results.map((r, i) => <ResultCard key={`${r.spot_number}-${i}`} r={r} i={i} />)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
